import { useState, useEffect } from "react";
import { PDFDocument } from "pdf-lib";
import { useMetriportApi } from "../../shared-logic/useMetriportApi";
import useMetriportToast from "../../shared-logic/useMetriportToast";
import { capture } from "../../../../shared/capture";

const STORAGE_KEY_PREFIX = "metriport_document_export_list";

type ExportDocument = {
  docId: string;
  docFilename: string;
};

type UseDocumentExport = {
  exportList: ExportDocument[];
  isExporting: boolean;
  isDocumentInExportList: (docId: string) => boolean;
  onAddToExport: (docId: string, docFilename: string) => void;
  removeDocument: (docId: string) => void;
  clearAll: () => void;
  exportCollatedPdf: (outputFilename: string) => Promise<void>;
};

export function useDocumentExport(patientId: string): UseDocumentExport {
  const [exportList, setExportList] = useState<ExportDocument[]>(() => loadFromStorage(patientId));
  const [isExporting, setIsExporting] = useState(false);
  const metriportApi = useMetriportApi();
  const toast = useMetriportToast();

  // Load the export list when patientId changes
  useEffect(() => {
    setExportList(loadFromStorage(patientId));
  }, [patientId]);

  // Save to localStorage whenever the list changes
  useEffect(() => {
    saveToStorage(patientId, exportList);
  }, [patientId, exportList]);

  function isDocumentInExportList(docId: string): boolean {
    return exportList.some(doc => doc.docId === docId);
  }

  function onAddToExport(docId: string, docFilename: string) {
    setExportList(prev => {
      const exists = prev.some(doc => doc.docId === docId);
      if (exists) {
        return prev;
      }
      return [...prev, { docId, docFilename }];
    });
  }

  function removeDocument(docId: string) {
    setExportList(prev => prev.filter(doc => doc.docId !== docId));
  }

  function clearAll() {
    setExportList([]);
    clearStorage(patientId);
  }

  async function exportCollatedPdf(outputFilename: string): Promise<void> {
    if (exportList.length === 0) {
      toast.warning({ title: "No documents to export" });
      return;
    }

    setIsExporting(true);
    toast.info({
      title: `Merging ${exportList.length} document(s)... This may take a moment.`,
      duration: 2000,
    });

    try {
      const mergedPdf = await PDFDocument.create();

      for (const doc of exportList) {
        try {
          const { url } = await metriportApi.getDocumentUrl(doc.docFilename, "pdf");
          const response = await fetch(url);
          const pdfBytes = await response.arrayBuffer();
          const pdfDoc = await PDFDocument.load(pdfBytes);
          const pageIndices = pdfDoc.getPageIndices();
          const pages = await mergedPdf.copyPages(pdfDoc, pageIndices);
          pages.forEach(page => mergedPdf.addPage(page));
        } catch (error) {
          capture.error(error, {
            extra: {
              context: "document-export.merge-pdf",
              docId: doc.docId,
              docFilename: doc.docFilename,
            },
          });
          toast.warning({ title: `Failed to merge: ${doc.docFilename}` });
        }
      }

      const mergedBytes = await mergedPdf.save();
      const filename = outputFilename.endsWith(".pdf") ? outputFilename : `${outputFilename}.pdf`;

      downloadBlob(mergedBytes, filename);

      toast.success({ title: `Successfully exported ${filename}` });
      clearAll(); // This also clears localStorage
    } catch (error) {
      capture.error(error, {
        extra: { context: "document-export.export-combined-pdf" },
      });
      toast.error({ title: "Failed to export combined PDF" });
    } finally {
      setIsExporting(false);
    }
  }

  return {
    exportList,
    isExporting,
    isDocumentInExportList,
    onAddToExport,
    removeDocument,
    clearAll,
    exportCollatedPdf,
  };
}

function downloadBlob(data: Uint8Array, filename: string) {
  const blob = new Blob([data.slice(0)], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function getStorageKey(patientId: string): string {
  return `${STORAGE_KEY_PREFIX}_${patientId}`;
}

function loadFromStorage(patientId: string): ExportDocument[] {
  try {
    const stored = localStorage.getItem(getStorageKey(patientId));
    if (!stored) return [];
    return JSON.parse(stored) as ExportDocument[];
  } catch (error) {
    console.error("Failed to load export list from localStorage:", error);
    return [];
  }
}

function saveToStorage(patientId: string, list: ExportDocument[]): void {
  try {
    localStorage.setItem(getStorageKey(patientId), JSON.stringify(list));
  } catch (error) {
    console.error("Failed to save export list to localStorage:", error);
  }
}

function clearStorage(patientId: string): void {
  try {
    localStorage.removeItem(getStorageKey(patientId));
  } catch (error) {
    console.error("Failed to clear export list from localStorage:", error);
  }
}
